// The phone's copy of the runner's transport and sources (C-transport, C-sources): outside Redux, since transport arrives at 10 Hz.

import { useSyncExternalStore } from 'react'
import { IS_DEV } from '~/lib/constants'
import desktopLink from '~/lib/desktopLink'
import store from '~/store'
import { selectIsRemote } from '~/store/app/selectors'
import { parsePins } from '~/lib/pinLayer'
import { receiveSourceSettings } from '~/store/live'
import { hydratePins, setCurrentPresetIndex } from '~/store/playback'
import { selectPlaylist } from '~/store/playback/selectors'
import { cycles, transportAt } from './model'

let snapshot = { transport: null, sources: null }
// Runner time of the newest transport applied: a late `ride` frame never overwrites a newer `ctl` one.
let newestT = -Infinity
let receivedAt = 0
const listeners = new Set()

const set = patch => {
	snapshot = { ...snapshot, ...patch }
	listeners.forEach(fn => fn())
}

const subscribe = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

const getSnapshot = () => snapshot
export { getSnapshot as getRemoteState, subscribe as onRemoteState }

const wraps = path => cycles(store.getState().live.modulators?.[path])

/** The transport moved on to now from its last message: for a rAF readout. Null before the first. */
export const transportNow = (now = performance.now()) => transportAt(snapshot.transport, receivedAt, now, wraps)

/** Runner time (`Date.now()` on the runner) the current transport was sent at. */
export const transportSentAt = () => newestT

/** `{ transport, sources }` as last received; re-renders per message, so rAF readouts use `transportNow`. */
export const useRemoteState = () => useSyncExternalStore(subscribe, getSnapshot)

let followed = { id: undefined, list: undefined }

// R12: a remote's current preset follows the runner's transport by id — an index, never a load. On a transport change, and on its playlist landing after one.
const followCurrent = () => {
	const s = store.getState()
	if (!selectIsRemote(s)) return
	const id = snapshot.transport?.current_id
	const list = selectPlaylist(s)
	if (id === followed.id && list === followed.list) return
	followed = { id, list }
	const i = id ? list.findIndex(p => p?.id === id) : -1
	if (i >= 0 && i !== s.playback.current_preset_index) store.dispatch(setCurrentPresetIndex(i))
}

let started = false

export const startRemoteFeed = () => {
	if (started) return
	started = true
	store.subscribe(followCurrent)
	desktopLink.on('transport', (payload, { t }) => {
		if (!(t >= newestT)) return
		newestT = t
		// `t` is the runner's clock: the link offset brings it onto this one, and the transit since is already elapsed (none counted before the first pong).
		const local = desktopLink.fromRunnerTime(t)
		const transit = local == null ? 0 : Math.max(0, Date.now() - local)
		receivedAt = performance.now() - transit
		set({ transport: payload })
		followCurrent()
	})
	desktopLink.on('sources', payload => set({ sources: payload }))
	desktopLink.on('pins', payload => store.dispatch(hydratePins({ project_id: null, pins: parsePins(payload) })))
	desktopLink.on('source_settings', payload =>
		store.dispatch({ ...receiveSourceSettings(payload), meta: { skipSync: true, skipHistory: true } })
	)
	let was = desktopLink.getSnapshot()
	desktopLink.onChange(now => {
		const opened = now.route !== 'down' && (was.route === 'down' || now.runner !== was.runner)
		if (now.runner !== was.runner) {
			newestT = -Infinity
			set({ transport: null, sources: null })
			store.dispatch(hydratePins({ project_id: null, pins: {} }))
		}
		was = now
		if (opened && now.role === 'remote') desktopLink.send('hello')
	})
}

if (IS_DEV) window.remoteState = { getSnapshot, transportNow }
